<template>
  <forgot-password-layout>
    <template slot="main">
      <section>
        <h1>{{ $_getTranslations.visitor_forgot_password_3_title }}</h1>

        <read-only-stepper-header :total-steps="3" :active-step="3"></read-only-stepper-header>

        <p>{{ $_getTranslations.visitor_forgot_password_3_description }}</p>

        <template v-if="isFacebookLoginVisible">
          <facebook-login
            :entered-username="facebookConfiguration.enteredUsername"
            :type="facebookConfiguration.type"
            @errorsUpdated="handleFacebookError($event)"
          ></facebook-login>

          <div v-if="facebookConfiguration.error" class="error_text par">
            {{ facebookConfiguration.error }}
          </div>

          <div class="divider"><span>{{ $_getTranslations.visitor_forgot_password_3_or }}</span></div>
        </template>

        <div v-if="serverResponseError" class="error_text">{{ serverResponseError }}</div>

        <base-password-form-input
          :model-value="formValues.password"
          :label="$_getTranslations.visitor_forgot_password_input_label_password"
          :invalid="passwordInputValidationConfiguration.invalid"
          @update:modelValue="handlePasswordChange"
        ></base-password-form-input>

        <div v-if="passwordInputValidationConfiguration.invalid" class="error_text">
          {{ passwordInputValidationConfiguration.message }}
        </div>

        <base-password-form-input
          :model-value="formValues.confirmPassword"
          :label="$_getTranslations.visitor_forgot_password_input_label_confirm_password"
          :invalid="confirmPasswordInputValidationConfiguration.invalid"
          input-name="confirmPassword"
          @update:modelValue="handleConfirmPasswordChange"
        ></base-password-form-input>

        <div v-if="confirmPasswordInputValidationConfiguration.invalid" class="error_text">
          {{ confirmPasswordInputValidationConfiguration.message }}
        </div>

        <base-checkbox-form-input
          v-model="formValues.rememberMe"
          :label="$_getTranslations.visitor_forgot_password_input_label_remember_me"
        ></base-checkbox-form-input>

        <base-button
          :disabled="isSubmitButtonDisabled"
          container-class="finish"
          @button:click.prevent="handleSubmit"
        >
          {{ $_getTranslations.visitor_forgot_password_button_finish }}
        </base-button>
      </section>
    </template>
  </forgot-password-layout>
</template>

<script>
import {mapGetters, mapActions, mapState} from 'vuex'
import ReadOnlyStepperHeader from '../../common/header/ReadOnlyStepperHeader'
import BasePasswordFormInput from '../../common/formInput/BasePasswordFormInput'
import BaseCheckboxFormInput from '../../common/formInput/BaseCheckboxFormInput'
import BaseButton from '../../common/button/BaseButton'
import FacebookLogin from '../FacebookLogin'
import ForgotPasswordLayout from './ForgotPasswordLayout'
import translationMixin from '../../../common/mixins/translation'
import {getApiResponseErrorMessage} from '../../../helpers/api'
import {forgotPasswordValidatePasswords} from '../../../helpers/forgotPassword'
import {apiResponseTypeEnum} from '../../../constants/api'
import {forgotPasswordErrorMessagesPerResponseType} from '../../../constants/forgotPassword'

export default {
  name: 'forgotPassword3',
  components: {
    BasePasswordFormInput,
    ReadOnlyStepperHeader,
    BaseCheckboxFormInput,
    BaseButton,
    FacebookLogin,
    ForgotPasswordLayout
  },
  mixins: [
    translationMixin
  ],
  data () {
    return {
      serverResponseError: '',
      facebookConfiguration: {enteredUsername: 'Login', type: 'login', error: ''},
      formValues: {
        password: '',
        confirmPassword: '',
        rememberMe: true
      },
      passwordInputValidationConfiguration: {invalid: false, message: ''},
      confirmPasswordInputValidationConfiguration: {invalid: false, message: ''}
    }
  },
  methods: {
    ...mapActions('profile', [
      'callForAuthorization',
      'resetPassword',
      'getUserLoginDetails'
    ]),
    handleFacebookError (event) {
      this.facebookConfiguration.error = this.$_translateText({key: event})
    },
    setInputFieldsErrors ({field, translatedMessage}) {
      const setError = (inputValidationConfiguration) => {
        inputValidationConfiguration.invalid = true
        inputValidationConfiguration.message = translatedMessage
      }

      if (field === 'all') {
        for (let inputValidationConfiguration of [this.passwordInputValidationConfiguration, this.confirmPasswordInputValidationConfiguration]) {
          setError(inputValidationConfiguration)
        }
      } else if (field === 'password') {
        setError(this.passwordInputValidationConfiguration)
      } else if (field === 'confirmPassword') {
        setError(this.confirmPasswordInputValidationConfiguration)
      }
    },
    validateForm () {
      const {invalid, configuration} = forgotPasswordValidatePasswords(this.formValues.password, this.formValues.confirmPassword, this.$_translateText)

      if (invalid) {
        this.setInputFieldsErrors(configuration)
      }

      return invalid
    },
    resetErrors () {
      if (this.facebookConfiguration.error) {
        this.facebookConfiguration.error = ''
      }

      if (this.serverResponseError) {
        this.serverResponseError = ''
      }

      for (let inputValidationConfiguration of [this.passwordInputValidationConfiguration, this.confirmPasswordInputValidationConfiguration]) {
        if (inputValidationConfiguration.invalid) {
          inputValidationConfiguration.invalid = false
          inputValidationConfiguration.message = ''
        }
      }
    },
    handlePasswordChange (value) {
      this.formValues.password = value

      if (this.passwordInputValidationConfiguration.invalid) {
        this.passwordInputValidationConfiguration.invalid = false
        this.passwordInputValidationConfiguration.message = ''
      }
    },
    handleConfirmPasswordChange (value) {
      this.formValues.confirmPassword = value

      if (this.confirmPasswordInputValidationConfiguration.invalid) {
        this.confirmPasswordInputValidationConfiguration.invalid = false
        this.confirmPasswordInputValidationConfiguration.message = ''
      }
    },
    handleSubmit () {
      this.resetErrors()

      const invalid = this.validateForm()

      if (invalid) { return }

      const userData = {
        msisdn: this.forgotPasswordInfo,
        password: this.formValues.confirmPassword
      }

      this.resetPassword(userData).then(resetPasswordResponse => {
        const {responseEnum: resetPasswordResponseEnum} = resetPasswordResponse.data

        if (resetPasswordResponseEnum === apiResponseTypeEnum.OPERATION_SUCCESS) {
          const userDataForAuthorization = {
            username: this.forgotPasswordInfo,
            password: this.formValues.confirmPassword,
            rememberMe: this.formValues.rememberMe
          }

          this.callForAuthorization(userDataForAuthorization).then(() => {
            if (this.getUserAuthorized) {
              this.getUserLoginDetails().then(() => {
                this.$router.push('/home')
              })
            }
          })
        } else {
          this.serverResponseError = this.$_translateText({
            key: getApiResponseErrorMessage(resetPasswordResponseEnum, forgotPasswordErrorMessagesPerResponseType)
          })
        }
      })
    }
  },
  computed: {
    ...mapState('common', [
      'facebookFunctionality'
    ]),
    ...mapState('profile', [
      'forgotPasswordInfo'
    ]),
    ...mapGetters('profile', [
      'getUserAuthorized'
    ]),
    isHomeMode () {
      this.$route.path.includes('home')
    },
    isFacebookLoginVisible () {
      return this.facebookFunctionality && !this.isHomeMode
    },
    isSubmitButtonDisabled () {
      return [this.formValues.password, this.formValues.confirmPassword].some(value => !value)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/visitor/forgotPassword/ForgotPassword3.vue